import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getPackagingInventory,
  addInventory,
  updateInventory,
  deleteInventory,
} from "../../../../../../apis/Business/InventoryApi";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import { getInventoryPackagingData } from "../../../../../../apis/Dropdown";
import { getTransportCountryData } from "../../../../../../apis/Dropdown";

const InventoryPackagingTable = ({ businessId, subcategory }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownData, setDropdownData] = useState([]);
  const [dropdownLocation, setDropdownLocation] = useState([]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getPackagingInventory({
      businessId: businessId,
      setObjects: setData,
      subcategory: subcategory,
    });
    getInventoryPackagingData({ setDropdownData: setDropdownData });
    getTransportCountryData({
      setDropdownData: setDropdownLocation,
    });
  }, [businessId, subcategory]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = useCallback(
    (editedItem) => {
      updateInventory({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        subcategory: subcategory,
        type_packaging: editedItem["type_packaging.value"]
          ? editedItem["type_packaging.value"].value
          : editedItem["type_packaging"]
          ? editedItem["type_packaging"].value
          : null,
        type_chemical: null,
        type_vehicle: null,
        type_buildingcode: null,
        type_service: null,
        type_material: null,
        type_machinery: null,
        type_buildingmaterial: null,
        subcategoryId: editedItem["subcategory_obj"],
        location: editedItem["location.value"]
          ? editedItem["location.value"].value
          : editedItem["location"]
          ? editedItem["location"].value
          : null,
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData, subcategory]
  );

  const handleAdd = useCallback(
    (newItem) => {
      addInventory({
        businessId,
        ...newItem,
        type_packaging: newItem["type_packaging.value"]
          ? newItem["type_packaging.value"].value
          : null,
        type_chemical: null,
        type_buildingcode: null,
        type_service: null,
        type_material: null,
        type_machinery: null,
        type_buildingmaterial: null,
        type_vehicle: null,
        subcategory: subcategory,
        location: newItem["location.value"]
          ? newItem["location.value"].value
          : null,
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData, subcategory]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteInventory({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "invoice_date",
        header: t("INVOICE_DATE"),
        type: "text",
        canHide: true,
        canSort: true,
        placeholder: t("DD_MM_YYYY"),
        optional: true,
        width: "80px",
      },
      {
        accessor: "invoice_number",
        header: t("INVOICE_NO"),
        type: "text",
        canSort: true,
        canHide: true,
        optional: true,
        width: "80px",
      },
      {
        accessor: "nickname",
        header: t("INV_NICKNAME"),
        type: "text",
        disableSortBy: true,
        placeholder: t("TYPE_HERE"),
        width: "80px",
      },
      {
        accessor: "location.value",
        header: t("PRODUCTION_COUNTRY"),
        type: "dropdown",
        dropdownData: dropdownLocation,
        disableSortBy: true,
        width: "110px",
        optional: true,
      },
      {
        accessor: "type_packaging.value",
        header: t("WRAPPING_PACKAGING"),
        type: "dropdown",
        dropdownData: dropdownData,
        disableSortBy: true,
        width: "110px",
        tipHeader: "Tip",
        tipBody: t("TIP_TYPE_MACHINERY"),
        isCategory: true,
        optional: true,
      },
      {
        accessor: "qty",
        header: t("AMOUNT"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        optional: false,
      },
      {
        accessor: "manuel",
        header: t("MANUEL"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        optional: true,
        tipHeader: "Tip",
        tipBody: t("TIP_PACKAGING_MANUEL"),
      },
      {
        accessor: "packaging_used_for",
        header: t("PACKAGING_USED_FOR"),
        type: "switch3",
        disableSortBy: true,
        tipHeader: "Tip",
        tipBody: t("PACKAGING_USED_FOR_TIP"),
        choice1: t("ORGANISATION"),
        choice2: t("PRODUCT"),
        choice3: "Service",
        width: "300px",
      },
      {
        accessor: "which_product_service",
        header: t("WHICH_PRODUCT_SERVICE"),
        type: "text",
        disableSortBy: true,
        placeholder: t("NAME_PRODUCT_SERVICE"),
        width: "80px",
        optional: "true",
        tipHeader: "Tip",
        tipBody: t("WHICH_PRODUCT_SERVICE_TIP"),
      },
      {
        accessor: "spacer2",
        header: "",
        type: "spacer",
        width: "20px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "price_invoice",
        header: t("PRICE_INVOICE"),
        type: "number",
        canSort: true,
        canHide: true,
        placeholder: "0",
        optional: true,
        subtotalUnit: "DKK",
        width: "80px",
        tipHeader: "Tip",
        tipBody: t("PRICE_INVOICE_TIP"),
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "80px",
      },
    ],
    [dropdownData, dropdownLocation, t]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    price_invoice: { label: t("TOTAL_PRICE") },
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      "type_packaging.value": dropdownData,
      "location.value": dropdownLocation,
    }),
    [dropdownData, dropdownLocation]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
    />
  );
};

export default InventoryPackagingTable;
